import './rater.subjectivities.list.scss';

import { Box, FormLabel, Stack, TableCell, TableRow } from '@mui/material';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';
import {
  FormSearchableSelectField,
  FormToggleSwitchField,
  NewFromSelectField,
} from '../../../../presentation';
import {
  HasSubjectivityInput,
  IUnderwritingSubjectivity,
  NoteTargetAreaType,
  SubjectivityDeadline,
  SubjectivityInput,
  SubjectivityType,
  UpsertCustomSubjectivityMutation,
  useGetSubjectivitiesQuery,
} from '@converge/graphql';
import { RaterNotesButton, RaterNotesRow } from '../common';
import { capitalizeWordsExceptPreposition, sortByOrder } from '@converge/utils';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { ApolloError } from '@apollo/client';
import { CreateSubjectivityField } from '@converge/data';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { RaterSubjectivitiesModal } from './rater.subjectivities.modal';
import { RaterSubjectivityModalBody } from './rater.subjectivity.modal.body';
import { RaterTableTitleCell } from '../rater.content.table';
import { useConvergeSnackBar } from '../../../../contexts';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

export interface CreateCustomSubjectivityField {
  [CreateSubjectivityField.customSubjectivityNameField]: string | undefined;
}
export const RaterSubjectivitiesList = ({
  mode = 'primary',
}: {
  mode: 'primary' | 'excess';
}) => {
  const { quoteUuid } = useParams<{ quoteUuid: string }>();
  const {
    columnCount,
    isEditable,
    upsertQuoteIsLoading,
    quoteSubjectivities,
    inForcePolicy,
  } = useRaterStateContextSelector(
    ({
      quote: { subjectivities: quoteSubjectivities },
      columnCount,
      isEditable,
      upsertQuoteIsLoading,
      quote: { inForcePolicy },
    }) => {
      return {
        columnCount,
        isEditable,
        upsertQuoteIsLoading,
        quoteSubjectivities,
        inForcePolicy,
      };
    }
  );

  const subjectivities = useMemo(() => {
    return [...(quoteSubjectivities ?? [])]
      .sort(sortByOrder)
      .filter((subjectivity) =>
        mode === 'primary' ? subjectivity.primary : !!subjectivity.excess
      );
  }, [mode, quoteSubjectivities]);

  const formMethods = useForm<CreateCustomSubjectivityField>({
    resolver: yupResolver({}),
    defaultValues: {},
  });

  const { actions, dispatch } = useRaterActionDispatchContext();
  const { notify } = useConvergeSnackBar();
  const { UpsertSubjectivity } = UpsertCustomSubjectivityMutation();

  const [isDisabledBtn, setIsDisabledBtn] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedSubjectivity, setSelectedSubjectivity] = useState<{
    name: string | undefined | null;
    uuid: string | undefined;
  } | null>(null);

  const handleClose = () => {
    formMethods.setValue(
      CreateSubjectivityField.customSubjectivityNameField,
      ''
    );
    setIsCreateModalOpen(false);
    setSelectedSubjectivity(null); // Close edit modal too
  };

  const handleSubmit = async (uuid?: string) => {
    let subjectivityResponseErrors;
    const subjectivityName = formMethods.getValues(
      CreateSubjectivityField.customSubjectivityNameField
    );

    const subjectivityData: {
      quote_uuid: string;
      subjectivity: SubjectivityInput;
      has_subjectivity: HasSubjectivityInput;
    } = {
      quote_uuid: quoteUuid || '',
      subjectivity: {
        uuid: uuid || '',
        type: SubjectivityType.Custom, // Use the proper enum value
        name: subjectivityName,
        description: '',
        afterPolicy: false,
        excess: true,

        order: 1000,
        primary: true,
        priorToBind: true, // THIS MIGHT BE LEGACY CODE
        priorToPolicy: true, // THIS MIGHT BE LEGACY CODE
      },
      has_subjectivity: {
        deadline: SubjectivityDeadline.PriorToBind,
      },
    };

    try {
      const { data, errors } = await UpsertSubjectivity({
        variables: subjectivityData,
      });
      if (uuid) {
        dispatch(
          actions.updateSubjectivity({
            update: true,
            ...(data?.upsertSubjectivity as IUnderwritingSubjectivity),
          })
        );
      } else {
        dispatch(
          actions.addSubjectivity({
            ...data?.upsertSubjectivity,
            quote: data?.upsertSubjectivity?.quote ?? {},
            isNew: true,
          })
        );
      }
      // else {
      //   dispatch(
      //     actions.updateSubjectivity({
      //       update: true,
      //       ...(data?.upsertSubjectivity as IUnderwritingSubjectivity),
      //     })
      //   )
      // }

      subjectivityResponseErrors = errors;
      notify(`Subjectivity has been added`, {
        variant: 'success',
      });
    } catch (cause) {
      console.error(
        `Failed to ${'save'} subjectivity. Please try again. ${cause}`
      );
      if (cause instanceof ApolloError) {
        subjectivityResponseErrors = cause.graphQLErrors;
      }
    }
    formMethods.setValue(
      CreateSubjectivityField.customSubjectivityNameField,
      ''
    );

    handleClose();

    const errorMessage = subjectivityResponseErrors?.[0]?.message;
    if (errorMessage) {
      notify(
        `Failed to ${'save'} subjectivity. Please try again. (${
          errorMessage || 'No data returned by the API'
        })`,
        {
          variant: 'error',
        }
      );

      return;
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Fragment>
        <TableRow className="rater-subjectivities-header">
          <RaterTableTitleCell columnCount={columnCount}>
            Subjectivities
          </RaterTableTitleCell>
        </TableRow>

        {subjectivities.map((subjectivity, index) => {
          if (subjectivity.uuid) {
            return (
              <RaterSubjectivityDetail
                key={index}
                customSubjectivityName={subjectivity?.name}
                uuid={subjectivity.uuid}
                subjectivity={subjectivity}
                handleSubmit={handleSubmit}
                selectedSubjectivity={selectedSubjectivity}
                setSelectedSubjectivity={setSelectedSubjectivity}
                handleClose={handleClose}
                isDisabledBtn={isDisabledBtn}
                setIsDisabledBtn={setIsDisabledBtn}
                formMethods={formMethods}
              />
            );
          }
          return false;
        })}

        {!subjectivities && (
          <TableRow>
            <TableCell align="center" colSpan={columnCount}>
              No subjectivities
            </TableCell>
          </TableRow>
        )}

        <RaterSubjectivityAddRow
          mode={mode}
          isDisabled={inForcePolicy || upsertQuoteIsLoading || !isEditable}
          handleSubmit={handleSubmit}
          setIsCreateModalOpen={setIsCreateModalOpen}
          isCreateModalOpen={isCreateModalOpen}
          handleClose={handleClose}
          isDisabledBtn={isDisabledBtn}
          setIsDisabledBtn={setIsDisabledBtn}
          formMethods={formMethods}
        />
      </Fragment>
    </FormProvider>
  );
};

interface IRaterSubjectivityAddRowProps {
  isDisabled: boolean;
  mode: 'primary' | 'excess';
  handleSubmit: (uuid?: string) => Promise<void>;
  setIsCreateModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsDisabledBtn: Dispatch<SetStateAction<boolean>>;
  isCreateModalOpen: boolean;
  isDisabledBtn: boolean;
  handleClose: () => void;
  formMethods: UseFormReturn<CreateCustomSubjectivityField, unknown>;
}

interface Subjectivity {
  name?: string | null | undefined;
  uuid?: string | null | undefined;
  __typename?: 'Subjectivity';
  order?: number | null;
  type?: SubjectivityType | null;
  description?: string | null;
  isActive?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  createdBy?: {
    uuid?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  updatedBy?: {
    uuid?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  questionMode?: string | null;
  quote?: {
    __typename?: 'HasSubjectivityType';
    uuid?: string | null | undefined;
    included?: boolean | null;
    createdAt?: Date | string;
    updatedAt?: Date | string;
  } | null;
}

const RaterSubjectivityAddRow = ({
  isDisabled,
  mode,
  handleSubmit,
  setIsCreateModalOpen,
  isCreateModalOpen,
  setIsDisabledBtn,
  isDisabledBtn,
  handleClose,
  formMethods,
}: IRaterSubjectivityAddRowProps) => {
  const { actions, dispatch } = useRaterActionDispatchContext();

  const { quoteSubjectivities, columnCount } = useRaterStateContextSelector(
    ({ quote: { subjectivities: quoteSubjectivities }, columnCount }) => ({
      quoteSubjectivities,
      columnCount,
    })
  );

  const { loading, subjectivities: fullSubjectivitiesList } =
    useGetSubjectivitiesQuery({
      uuid: null,
      name: null,
      type: null,
      pagination: null,
    });
  const fullSubjectivitiesListForMode = useMemo(() => {
    return fullSubjectivitiesList?.filter(
      (subjectivity) =>
        (mode === 'primary' ? !!subjectivity.primary : !!subjectivity.excess) &&
        !quoteSubjectivities.some(({ uuid }) => subjectivity.uuid === uuid)
    );
  }, [fullSubjectivitiesList, mode, quoteSubjectivities]);

  const updatedFullSubjectivitiesListForMode: Subjectivity[] = [
    {
      name: 'Other (Custom Subjectivity)',
      uuid: null,
      __typename: 'Subjectivity',
    },
    ...fullSubjectivitiesListForMode,
  ];

  return (
    <TableRow>
      <TableCell colSpan={3}>
        <FormSearchableSelectField
          minDropdownWidth="350px"
          maxDropdownWidth="450px"
          disabled={isDisabled}
          options={updatedFullSubjectivitiesListForMode}
          isLoading={loading}
          mapToOption={(subjectivity) => {
            return {
              value: subjectivity?.uuid ?? '',
              label: subjectivity?.name ?? 'N/A',
            };
          }}
          name=""
          resetAfterSelect
          placeholder="Select subjectivity to add"
          onChangeOption={({ data: { quote, ...others } }) => {
            if (others.uuid) {
              dispatch(
                actions.addSubjectivity({
                  ...others,
                  quote: {
                    ...quote,
                    included: true,
                  } as IUnderwritingSubjectivity['quote'],
                  isNew: true,
                })
              );
            } else {
              setIsCreateModalOpen(true);
            }
          }}
        />
        <RaterSubjectivitiesModal
          handleSubmit={() => handleSubmit()}
          open={isCreateModalOpen}
          onClose={handleClose}
          title={
            <Box className="modal-title-without-icon ">Custom Subjectivity</Box>
          }
          body={
            <RaterSubjectivityModalBody
              setIsDisabled={setIsDisabledBtn}
              formMethods={formMethods}
              subjectivityUuid={''}
              subjectivityName={''}
            />
          }
          btmLabel="SAVE"
          isDisabled={isDisabledBtn}
          setIsDisabledBtn={setIsDisabledBtn}
        />
      </TableCell>
      <TableCell colSpan={columnCount - 3}></TableCell>
    </TableRow>
  );
};

interface IRaterSubjectivityDetailProps {
  subjectivity: IUnderwritingSubjectivity;
  inForcePolicy?: boolean;
  handleSubmit: (uuid?: string) => Promise<void>;
  subjectivityUuid?: string;

  setIsDisabledBtn: Dispatch<SetStateAction<boolean>>;
  selectedSubjectivity: {
    name: string | undefined | null;
    uuid: string | undefined;
  } | null;
  isDisabledBtn: boolean;
  handleClose: () => void;
  customSubjectivityName?: string | null | undefined;
  uuid?: string | undefined;
  formMethods: UseFormReturn<CreateCustomSubjectivityField, unknown>;
  setSelectedSubjectivity: Dispatch<
    SetStateAction<{
      name: string | undefined | null;
      uuid: string | undefined;
    } | null>
  >;
}

const DEADLINE_OPTIONS = Object.entries(SubjectivityDeadline)
  .map(([, label]) => {
    switch (label) {
      case SubjectivityDeadline.PriorToBind:
        return {
          value: label,
          label: 'Prior to bind',
          order: 0,
        };
      case SubjectivityDeadline.PriorToPolicy:
        return {
          value: label,
          label: 'Prior to policy issuance',
          order: 1,
        };
      case SubjectivityDeadline.AfterPolicy:
        return {
          value: label,
          label: 'After policy issuance',
          order: 2,
        };
      default:
        return {
          value: label,
          label,
          order: 3,
        };
    }
  })
  .sort((a, b) => a.order - b.order);

const RaterSubjectivityDetail = ({
  subjectivity: {
    name,
    type,
    quote: { uuid: quoteUuid },
    priorToBind,
    priorToPolicy,
    afterPolicy,
    hasChanged,
  },
  subjectivity,
  handleSubmit,

  setIsDisabledBtn,
  isDisabledBtn,
  handleClose,
  selectedSubjectivity,
  setSelectedSubjectivity,
  customSubjectivityName,
  uuid,
  formMethods,
}: IRaterSubjectivityDetailProps) => {
  const {
    isEditable,
    columnCount,
    upsertQuoteIsLoading,
    isRequestingRatingUpdate,
    inForcePolicy,
  } = useRaterStateContextSelector(
    ({
      isEditable,
      columnCount,
      upsertQuoteIsLoading,
      isRequestingRatingUpdate,
      quote: { inForcePolicy },
    }) => {
      return {
        isEditable,
        columnCount,
        upsertQuoteIsLoading,
        isRequestingRatingUpdate,
        inForcePolicy,
      };
    }
  );

  const { actions, dispatch } = useRaterActionDispatchContext();

  const deadlineOptions = useMemo(() => {
    return DEADLINE_OPTIONS.filter((option) => {
      switch (option.value) {
        case SubjectivityDeadline.PriorToBind:
          return priorToBind;
        case SubjectivityDeadline.PriorToPolicy:
          return priorToPolicy;
        case SubjectivityDeadline.AfterPolicy:
          return afterPolicy;
        default:
          return false;
      }
    });
  }, [priorToBind, priorToPolicy, afterPolicy]);

  useEffect(() => {
    if (!subjectivity.quote.deadline && deadlineOptions.length > 0) {
      dispatch(
        actions.updateSubjectivity({
          ...subjectivity,
          quote: {
            ...subjectivity.quote,
            deadline: deadlineOptions[0].value,
          },
          update: true,
        })
      );
    }
  }, [actions, deadlineOptions, dispatch, subjectivity]);

  return (
    <Fragment>
      <TableRow
        className={`table-row-collapsible`}
        sx={{
          backgroundColor: hasChanged ? 'rgb(253, 245, 231, 0.75)' : undefined,
        }}
      >
        <TableCell className="rater-subjectivities-name" colSpan={2}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box>
              {type === SubjectivityType.Custom && (
                <EditNoteIcon
                  fontSize="small"
                  style={{ marginRight: '2px', paddingTop: '6px' }}
                  onClick={() => setSelectedSubjectivity({ name, uuid })}
                />
              )}
            </Box>
            <Box style={{ fontWeight: 'bold' }}>
              {capitalizeWordsExceptPreposition(name ?? '')}
            </Box>
          </Box>
          <RaterSubjectivitiesModal
            handleSubmit={() => handleSubmit(selectedSubjectivity?.uuid)}
            open={!!selectedSubjectivity}
            onClose={handleClose}
            title={
              <Box className="modal-title-without-icon ">
                Custom Subjectivity
              </Box>
            }
            body={
              selectedSubjectivity && (
                <RaterSubjectivityModalBody
                  setIsDisabled={setIsDisabledBtn}
                  subjectivityUuid={selectedSubjectivity.uuid || ''}
                  subjectivityName={selectedSubjectivity.name || ''}
                  formMethods={formMethods}
                />
              )
            }
            btmLabel="SAVE"
            isDisabled={isDisabledBtn}
            setIsDisabledBtn={setIsDisabledBtn}
          />
        </TableCell>
        <TableCell>
          <FormToggleSwitchField.Switch
            onChange={(event, included) => {
              dispatch(
                actions.updateSubjectivity({
                  ...subjectivity,
                  quote: {
                    ...subjectivity.quote,
                    included: included,
                  },
                  update: true,
                })
              );
            }}
            checked={subjectivity?.quote?.included ?? false}
            disabled={
              isRequestingRatingUpdate ||
              upsertQuoteIsLoading ||
              !isEditable ||
              inForcePolicy
            }
          />
        </TableCell>
        <TableCell colSpan={columnCount - 4}>
          <Stack gap={2} className="rater-subjectivities-deadline">
            <Box>
              <FormLabel>Deadline</FormLabel>
            </Box>
            <Box style={{ width: '200px' }}>
              <NewFromSelectField
                disabled={
                  isRequestingRatingUpdate ||
                  upsertQuoteIsLoading ||
                  !isEditable ||
                  deadlineOptions.length <= 1 ||
                  inForcePolicy
                }
                placeholder={
                  deadlineOptions.length === 0
                    ? 'No deadline options'
                    : 'Select deadline'
                }
                hideLabel
                name={''}
                options={deadlineOptions}
                value={subjectivity.quote.deadline}
                onChangeOption={(option) => {
                  dispatch(
                    actions.updateSubjectivity({
                      ...subjectivity,
                      quote: {
                        ...subjectivity.quote,
                        deadline: Object.values(SubjectivityDeadline).find(
                          (deadline) => deadline === option.value
                        ),
                      },
                      update: false,
                    })
                  );
                }}
              />
            </Box>
            <Box flexGrow={1} className="non-premium-container ">
              Non-premium bearing
            </Box>
          </Stack>
        </TableCell>
        <TableCell aria-label="Notes">
          <RaterNotesButton
            uuid={quoteUuid ?? ''}
            targetArea={NoteTargetAreaType.Subjectivity}
            // targetName={NoteTargetName.HasSubjectivityType}
            // targetType={NoteTargetType.Relationship}
          />
        </TableCell>
      </TableRow>
      <RaterNotesRow
        targetArea={NoteTargetAreaType.Subjectivity}
        // targetName={NoteTargetName.HasSubjectivityType}
        // targetType={NoteTargetType.Relationship}
        uuid={quoteUuid ?? ''}
        colSpan={columnCount}
      />
    </Fragment>
  );
};
